import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaCrown, FaShieldAlt, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import api from '../../../services/api';
import { useCloudflareAuth } from '../../../contexts/CloudflareAuthContext';

declare global {
  interface Window { Razorpay?: any; }
}

interface Plan {
  id: string;
  name: string;
  member_type: string;
  baseAmount: number;
  maintenanceAmount: number;
  feePercent: number;
  gatewayFee: number;
  totalAmount: number;
}

const money = (paise: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(paise / 100);

const loadRazorpay = () => new Promise<boolean>((resolve) => {
  if (window.Razorpay) return resolve(true);
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

const MembershipPlans: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userDetails, refreshUserDetails } = useCloudflareAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState('');

  useEffect(() => {
    api.getMembershipPlans()
      .then(response => setPlans(response.plans || []))
      .catch((error) => toast.error(error.message || 'Could not load membership plans'))
      .finally(() => setLoading(false));
  }, []);

  const choosePlan = async (plan: Plan) => {
    setPaying(plan.id);
    try {
      if (!(await loadRazorpay())) throw new Error('Razorpay checkout could not be loaded');
      const response = await api.createMembershipOrder(plan.id);
      const checkout = new window.Razorpay({
        key: response.keyId,
        amount: response.order.amount,
        currency: response.order.currency,
        name: 'CCRO India',
        description: `${plan.name} — Lifetime Membership`,
        order_id: response.order.id,
        prefill: { name: userDetails?.fullName || currentUser?.displayName || '', email: currentUser?.email || '', contact: userDetails?.phone || '' },
        theme: { color: '#3b82f6', backdrop_color: '#020617' },
        modal: { ondismiss: () => setPaying('') },
        handler: async (payment: any) => {
          try {
            await api.verifyMembershipPayment(payment);
            await refreshUserDetails();
            toast.success('Membership payment completed');
            navigate('/dashboard/user-details', { replace: true });
          } catch (error: any) {
            toast.error(error.message || 'Payment verification failed');
            setPaying('');
          }
        },
      });
      checkout.on('payment.failed', (failure: any) => {
        toast.error(failure.error?.description || 'Payment failed');
        setPaying('');
      });
      checkout.open();
    } catch (error: any) {
      toast.error(error.message || 'Could not start payment');
      setPaying('');
    }
  };

  if (loading) return <div className="flex min-h-[60vh] items-center justify-center"><FaSpinner className="animate-spin text-4xl text-blue-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-10 text-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600"><FaCrown className="text-2xl" /></div>
          <p className="mb-2 text-sm font-semibold uppercase tracking-[0.25em] text-blue-400">Lifetime Member of Organization</p>
          <h1 className="text-3xl font-bold sm:text-4xl">Choose your membership</h1>
          <p className="mt-3 text-slate-400">One-time lifetime membership fee. Continue to your details after successful payment.</p>
        </div>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {plans.map((plan, index) => (
            <article key={plan.id} className={`relative overflow-hidden rounded-2xl border bg-slate-900/80 p-6 shadow-xl ${index === plans.length - 1 ? 'border-purple-500/50' : 'border-white/10'}`}>
              <FaShieldAlt className="mb-5 text-2xl text-blue-400" />
              <h2 className="text-xl font-bold">{plan.name}</h2>
              <p className="mt-1 text-sm text-slate-500">Lifetime membership</p>
              <div className="my-6 space-y-3 border-y border-white/10 py-5 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Membership fee</span><span>{money(plan.baseAmount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Maintenance</span><span>{money(plan.maintenanceAmount)}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Gateway fee ({plan.feePercent}%)</span><span>{money(plan.gatewayFee)}</span></div>
              </div>
              <div className="mb-5 flex items-end justify-between"><span className="text-slate-400">Total</span><strong className="text-2xl text-blue-300">{money(plan.totalAmount)}</strong></div>
              <button onClick={() => choosePlan(plan)} disabled={Boolean(paying)} className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 px-4 py-3 font-semibold transition hover:brightness-110 disabled:opacity-50">
                {paying === plan.id ? <><FaSpinner className="animate-spin" /> Opening payment…</> : <><FaCheck /> Choose & Pay</>}
              </button>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MembershipPlans;
